import type {Component, Editor} from 'grapesjs';
import {InsertPosition} from '@Components/email-maker-block-manager/components/add-block-menu/add-block-menu.types';

export const initBorderControls = (editor: Editor): void => {
  const attachedComponents = new WeakMap<Component, boolean>();

  const createAddElementButton = (position: 'top' | 'bottom'): HTMLElement => {
    const button = document.createElement('div');
    button.className = `pmw-add-element-button ${position}`;
    button.setAttribute('data-position', position);
    return button;
  };

  const renderAddElementMenuInContainer = (anchorElement: HTMLElement, position: 'above' | 'below'): void => {
    const existingContainer = document.querySelector('.pmw-add-element-menu-container');
    if (existingContainer) {
      existingContainer.remove();
    }

    const canvasBody = editor.Canvas.getBody();
    if (canvasBody) {
      const previousActiveButtons = canvasBody.querySelectorAll('.pmw-add-element-button.-active');
      previousActiveButtons.forEach((btn) => {
        btn.classList.remove('-active');
      });
    }

    anchorElement.classList.add('-active');

    const container = document.createElement('div');
    container.className = 'pmw-add-element-menu-container';
    container.style.position = 'absolute';
    container.style.zIndex = '1000';

    const frameEl = editor.Canvas.getFrameEl();
    if (!frameEl) {
      return;
    }

    const rect = anchorElement.getBoundingClientRect();
    const frameRect = frameEl.getBoundingClientRect();

    const top = position === 'above' ? rect.top : rect.bottom;
    const left = rect.left;

    container.style.top = `${frameRect.top + top + window.scrollY}px`;
    container.style.left = `${frameRect.left + left + window.scrollX}px`;

    if (!document.body) {
      return;
    }

    document.body.appendChild(container);

    const originalRemove = container.remove.bind(container);
    container.remove = function (): void {
      anchorElement.classList.remove('-active');
      originalRemove();
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    window.PMW.renderAddElementMenu(
      container,
      {
        insertPosition: position === 'above' ? InsertPosition.ABOVE : InsertPosition.BELOW,
      },
      editor
    );
  };

  const repositionBorderControlButtons = (element: HTMLElement, canvasBody: HTMLElement, elementId: string): void => {
    const buttons = canvasBody.querySelectorAll(`[data-target-element-id="${elementId}"]`);
    const rect = element.getBoundingClientRect();
    const canvasRect = canvasBody.getBoundingClientRect();

    buttons.forEach((btn) => {
      const htmlBtn = btn as HTMLElement;
      const position = htmlBtn.getAttribute('data-position');

      htmlBtn.style.position = 'absolute';
      htmlBtn.style.left = `${rect.left - canvasRect.left + rect.width / 2}px`;
      htmlBtn.style.transform = 'translate(-50%, -50%)';
      htmlBtn.style.zIndex = '10';

      if (position === 'top') {
        htmlBtn.style.top = `${rect.top - canvasRect.top}px`;
      } else if (position === 'bottom') {
        htmlBtn.style.top = `${rect.bottom - canvasRect.top}px`;
      }
    });
  };

  const attachAddElementButtons = (element: HTMLElement, component: Component): void => {
    if (!element || !(element instanceof HTMLElement)) {
      return;
    }

    if (attachedComponents.has(component)) {
      return;
    }

    const shouldSkipButtons =
      component.is(window.PMW.emails.components.table.PMW_CELL) ||
      component.is(window.PMW.emails.components.table.PMW_ROW) ||
      component.is(window.PMW.emails.components.table.PMW_TBODY);

    if (shouldSkipButtons) {
      return;
    }

    const canvasBody = editor.Canvas.getBody();
    if (!canvasBody) {
      return;
    }

    const elementId = element.id || component.getId();
    if (!element.id) {
      element.id = elementId;
    }

    attachedComponents.set(component, true);

    const existingButtons = canvasBody.querySelectorAll(`[data-target-element-id="${elementId}"]`);
    existingButtons.forEach((btn) => {
      btn.remove();
    });

    const topButton = createAddElementButton('top');
    const bottomButton = createAddElementButton('bottom');

    topButton.setAttribute('data-target-element-id', elementId);
    bottomButton.setAttribute('data-target-element-id', elementId);

    topButton.style.display = 'none';
    bottomButton.style.display = 'none';

    canvasBody.appendChild(topButton);
    canvasBody.appendChild(bottomButton);

    const updateButtonVisibilityForScreenSize = (): void => {
      const isSmallScreen = window.innerWidth <= 768;
      if (isSmallScreen) {
        topButton.classList.add('_hidden');
        bottomButton.classList.add('_hidden');
      } else {
        topButton.classList.remove('_hidden');
        bottomButton.classList.remove('_hidden');
      }
    };

    updateButtonVisibilityForScreenSize();

    const handleResize = (): void => {
      updateButtonVisibilityForScreenSize();
    };

    window.addEventListener('resize', handleResize);

    const updateButtonPositions = (): void => {
      const rect = element.getBoundingClientRect();
      const canvasRect = canvasBody.getBoundingClientRect();

      const calculatedLeft = rect.left - canvasRect.left + rect.width / 2;
      const calculatedTop = rect.top - canvasRect.top;
      const calculatedBottom = rect.bottom - canvasRect.top;

      if (!calculatedLeft || !calculatedBottom || !calculatedTop) {
        return;
      }

      topButton.style.position = 'absolute';
      topButton.style.left = `${calculatedLeft}px`;
      topButton.style.top = `${calculatedTop}px`;
      topButton.style.transform = 'translate(-50%, -50%)';
      topButton.style.zIndex = '10';

      bottomButton.style.position = 'absolute';
      bottomButton.style.left = `${calculatedLeft}px`;
      bottomButton.style.top = `${calculatedBottom}px`;
      bottomButton.style.transform = 'translate(-50%, -50%)';
      bottomButton.style.zIndex = '10';
    };

    const showButtons = (): void => {
      topButton.style.display = 'flex';
      bottomButton.style.display = 'flex';
      updateButtonPositions();
    };

    const hideButtons = (): void => {
      if (component.getId() !== editor.getSelected()?.getId()) {
        topButton.style.display = 'none';
        bottomButton.style.display = 'none';
      }
    };

    const handleButtonMouseEnter = (): void => {
      showButtons();
    };

    const handleButtonMouseLeave = (e: MouseEvent): void => {
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (relatedTarget === element || element.contains(relatedTarget)) {
        return;
      }
      hideButtons();
    };

    updateButtonPositions();

    const handleComponentSelected = (selectedComponent: Component): void => {
      if (selectedComponent && component && selectedComponent.getId() === component.getId()) {
        showButtons();
      }
    };

    const handleComponentDeselected = (deselectedComponent: Component): void => {
      if (deselectedComponent && component && deselectedComponent.getId() === component.getId()) {
        hideButtons();
      }
    };

    editor.on('component:selected', handleComponentSelected);
    editor.on('component:deselected', handleComponentDeselected);

    topButton.addEventListener('mouseenter', handleButtonMouseEnter);
    topButton.addEventListener('mouseleave', handleButtonMouseLeave);
    bottomButton.addEventListener('mouseenter', handleButtonMouseEnter);
    bottomButton.addEventListener('mouseleave', handleButtonMouseLeave);

    if (editor.getSelected() === component) {
      showButtons();
    }

    const handleButtonClick = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      const button = e.currentTarget as HTMLElement;
      const position = button.getAttribute('data-position');

      renderAddElementMenuInContainer(button, position === 'top' ? 'above' : 'below');
    };

    topButton.addEventListener('click', handleButtonClick);
    bottomButton.addEventListener('click', handleButtonClick);

    const observer = new MutationObserver(() => {
      updateButtonPositions();
    });

    observer.observe(element, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const scrollHandler = (): void => {
      updateButtonPositions();
    };

    canvasBody.addEventListener('scroll', scrollHandler, true);

    const cleanup = (): void => {
      topButton.remove();
      bottomButton.remove();
      observer.disconnect();
      canvasBody.removeEventListener('scroll', scrollHandler, true);
      window.removeEventListener('resize', handleResize);
      editor.off('component:selected', handleComponentSelected);
      editor.off('component:deselected', handleComponentDeselected);
      topButton.removeEventListener('mouseenter', handleButtonMouseEnter);
      topButton.removeEventListener('mouseleave', handleButtonMouseLeave);
      bottomButton.removeEventListener('mouseenter', handleButtonMouseEnter);
      bottomButton.removeEventListener('mouseleave', handleButtonMouseLeave);
    };

    component.on('component:remove', cleanup);
  };

  const handleComponentReposition = (component: Component): void => {
    const element = component.getEl();

    if (!element || !component.get(window.PMW.emails.props.HAS_BORDER_CONTROLS)) {
      return;
    }

    if (!attachedComponents.has(component)) {
      return;
    }

    const canvasBody = editor.Canvas.getBody();
    if (!canvasBody) {
      return;
    }

    const elementId = element.id || component.getId();
    repositionBorderControlButtons(element, canvasBody, elementId);
  };

  editor.on('component:mount', (component: Component) => {
    const element = component.getEl();

    if (element && element instanceof HTMLElement && component.get(window.PMW.emails.props.HAS_BORDER_CONTROLS)) {
      attachAddElementButtons(element, component);
    }
  });

  editor.on('component:update', handleComponentReposition);
  editor.on('component:update:position', handleComponentReposition);
};
